import AppComponent from '../base/app-component.ts';

type CodiceCassa = string;

interface CassaConfig {
  codice: string;
  nome: string;
}

interface RangeState {
  loading: boolean;
  error: string | null;
  range: {
    periodoInizio: string;
    periodoFine: string;
    nPeriodi: number;
  } | null;
}

interface CassaView {
  codice: CodiceCassa;
  nome: string;
  loading: boolean;
  error: string | null;
  hasRange: boolean;
  periodoInizio: string;
  periodoFine: string;
  nPeriodi: number;
}

interface CasseConfigResponse {
  codice: string;
  nome: string;
  descrizione: string | null;
  estensione_archivio: string;
  codice_fenice: number;
  archivio_multiplo: boolean;
}

export class StatisticheSetup extends AppComponent {
  static override componentName = 'wc-statistiche-setup';

  protected override get templateId() { return 'statistiche-setup'; }

  /** Casse caricate da /api/casse/config (sostituisce array hardcoded). */
  private casse: CassaConfig[] = [];
  /** Stato per cassa: chiave = codice cassa. */
  private state: Record<CodiceCassa, RangeState> = {};
  private eseguendo: CodiceCassa | null = null;

  protected override onConnected(): void {
    this.render();
    void this.bootstrap();
  }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      casse: this.casse.map(c => this.buildCassaView(c.codice, c.nome)),
      overlay: this.eseguendo !== null,
    };
  }

  protected override afterRender(): void {
    this.delegate.on('click', '[data-action="esegui"]', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="esegui"]') as HTMLElement;
      const cassa = btn.dataset.cassa;
      if (!cassa) return;
      void this.esegui(cassa);
    });
  }

  /** Carica config casse + range per ciascuna cassa, poi rerender. */
  private async bootstrap(): Promise<void> {
    const cfg = await fetch('/api/casse/config', { credentials: 'same-origin' });
    if (!cfg.ok) {
      this.casse = [];
      this.render();
      return;
    }
    const list = (await cfg.json()) as CasseConfigResponse[];
    this.casse = list.map(c => ({ codice: c.codice, nome: `${c.nome} (${c.codice})` }));
    for (const c of this.casse) {
      this.state[c.codice] = { loading: true, error: null, range: null };
    }
    this.render();
    await Promise.all(this.casse.map(c => this.caricaCassa(c.codice)));
  }

  private async caricaCassa(cassa: CodiceCassa): Promise<void> {
    const result = await this.api.statistiche.range(cassa);
    const slot = this.state[cassa];
    if (!slot) return;
    slot.loading = false;

    if (!result.ok) {
      slot.error = result.error;
      this.render();
      return;
    }

    slot.range = result.data.range
      ? {
          periodoInizio: result.data.range.periodoInizio,
          periodoFine: result.data.range.periodoFine,
          nPeriodi: result.data.range.nPeriodi,
        }
      : null;
    this.render();
  }

  private async esegui(cassa: CodiceCassa): Promise<void> {
    const range = this.state[cassa]?.range;
    if (!range || range.nPeriodi < 1) return;

    this.eseguendo = cassa;
    this.render();

    const result = await this.api.statistiche.calcola(cassa);
    if (!result.ok) {
      this.eseguendo = null;
      this.render();
      (globalThis as unknown as { Weext?: { toast: (msg: string, opts?: unknown) => void } }).Weext
        ?.toast(result.error, { type: 'danger' });
      return;
    }

    window.location.href = `/statistiche/${result.data.nomeTabella}`;
  }

  private buildCassaView(codice: CodiceCassa, nome: string): CassaView {
    const slot = this.state[codice] ?? { loading: true, error: null, range: null };
    const { loading, error, range } = slot;
    return {
      codice,
      nome,
      loading,
      error,
      hasRange: !!range,
      periodoInizio: range?.periodoInizio ?? '',
      periodoFine: range?.periodoFine ?? '',
      nPeriodi: range?.nPeriodi ?? 0,
    };
  }
}
